import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ContactRound, Pencil, Plus, RotateCcw, UserMinus } from "lucide-react";
import { toast } from "sonner";

import { api, toastError } from "../../lib/api";
import { hasActionAccess } from "../../lib/workspaces";
import { useAuth } from "../../context/AuthContext";
import { PageHeader } from "../../components/domain/Bits";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

type Link = { id: string; name: string };
type DirectoryContact = {
  id: string; full_name: string; role_type: string; email?: string; phone?: string;
  notes?: string; contact_ids: string[]; organization_ids: string[]; owner_id?: string;
  is_primary: boolean; start_date?: string; end_date?: string; active: boolean; version: number;
  linked_contacts: Link[]; linked_organizations: Link[];
};
type Choice = { id: string; name?: string; first_name?: string; last_name?: string };
type Form = {
  full_name: string; role_type: string; email: string; phone: string; notes: string;
  contact_ids: string[]; organization_ids: string[]; owner_id: string;
  is_primary: boolean; start_date: string; end_date: string;
};
const blank: Form = {
  full_name: "", role_type: "MOA", email: "", phone: "", notes: "",
  contact_ids: [], organization_ids: [], owner_id: "", is_primary: false,
  start_date: "", end_date: "",
};
const roles = ["MOA", "Executive Assistant", "Receptionist", "Clinic Manager", "Office Manager", "Nurse", "Guard", "Other"];

export default function RelationshipContacts() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const linkedContactId = searchParams.get("contact_id") || undefined;
  const linkedOrganizationId = searchParams.get("organization_id") || undefined;
  const [rows, setRows] = useState<DirectoryContact[]>([]);
  const [contacts, setContacts] = useState<Choice[]>([]);
  const [organizations, setOrganizations] = useState<Choice[]>([]);
  const [active, setActive] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<DirectoryContact | null>(null);
  const [form, setForm] = useState<Form>(blank);
  const [saving, setSaving] = useState(false);

  const canAdd = hasActionAccess(user, "relationship_contacts", "add");
  const canEdit = hasActionAccess(user, "relationship_contacts", "edit");
  const canDeactivate = hasActionAccess(user, "relationship_contacts", "deactivate");
  const canRestore = hasActionAccess(user, "relationship_contacts", "restore");

  const load = async () => {
    const [directory, doctorRows, orgRows] = await Promise.all([
      api.get("/crm/relationship-contacts", { params: { active, contact_id: linkedContactId, organization_id: linkedOrganizationId } }),
      api.get("/crm/contacts", { params: { limit: 500 } }),
      api.get("/crm/organizations", { params: { limit: 500 } }),
    ]);
    setRows(directory.data);
    setContacts(doctorRows.data);
    setOrganizations(orgRows.data);
  };
  useEffect(() => { load().catch(() => toast.error("Could not load relationship contacts")); }, [active, linkedContactId, linkedOrganizationId]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) => [
      row.full_name, row.role_type, row.email, row.phone,
      ...row.linked_contacts.map((item) => item.name),
      ...row.linked_organizations.map((item) => item.name),
    ].some((value) => (value || "").toLowerCase().includes(needle)));
  }, [rows, q]);

  const startAdd = () => {
    setEditing(null);
    setForm({
      ...blank,
      contact_ids: linkedContactId ? [linkedContactId] : [],
      organization_ids: linkedOrganizationId ? [linkedOrganizationId] : [],
    });
    setOpen(true);
  };
  const startEdit = (row: DirectoryContact) => {
    setEditing(row);
    setForm({
      full_name: row.full_name, role_type: row.role_type, email: row.email || "",
      phone: row.phone || "", notes: row.notes || "", contact_ids: row.contact_ids || [],
      organization_ids: row.organization_ids || [], owner_id: row.owner_id || "",
      is_primary: row.is_primary, start_date: row.start_date || "", end_date: row.end_date || "",
    });
    setOpen(true);
  };
  const toggleValues = (event: React.ChangeEvent<HTMLSelectElement>) =>
    Array.from(event.target.selectedOptions).map((option) => option.value);

  const save = async () => {
    if (!form.full_name.trim() || !form.role_type.trim()) return;
    if (!form.contact_ids.length && !form.organization_ids.length) {
      toast.error("Link at least one doctor or organization");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        full_name: form.full_name.trim(), role_type: form.role_type.trim(),
        email: form.email || null, phone: form.phone || null, notes: form.notes || null,
        owner_id: form.owner_id || null, start_date: form.start_date || null, end_date: form.end_date || null,
      };
      if (editing) await api.put(`/crm/relationship-contacts/${editing.id}`, { ...payload, version: editing.version });
      else await api.post("/crm/relationship-contacts", payload);
      toast.success(editing ? "Relationship contact updated" : "Relationship contact added");
      setOpen(false);
      await load();
    } catch (error: any) {
      toastError(error, "Could not save relationship contact");
    } finally { setSaving(false); }
  };

  const changeLifecycle = async (row: DirectoryContact) => {
    try {
      await api.post(`/crm/relationship-contacts/${row.id}/${row.active ? "deactivate" : "reactivate"}`);
      toast.success(row.active ? "Contact deactivated; history preserved" : "Contact reactivated");
      await load();
    } catch { toast.error("Could not update contact status"); }
  };

  return (
    <>
      <PageHeader
        title="Relationship Contacts"
        subtitle={linkedContactId ? "Contact people linked to this doctor or lead." : linkedOrganizationId ? "Contact people linked to this organization or clinic." : "MOAs, assistants, receptionists, managers, and other people connected to doctors and organizations."}
        actions={canAdd ? <Button onClick={startAdd} data-testid="add-relationship-contact"><Plus className="w-4 h-4 mr-1" /> Add contact person</Button> : undefined}
      />
      <main className="max-w-7xl mx-auto p-6 md:p-8 space-y-4">
        {!canAdd && !canEdit && !canDeactivate && !canRestore && (
          <div className="rounded-md bg-slate-50 px-4 py-3 text-sm text-slate-600">View only — relationship contact changes are disabled for your account.</div>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <Input value={q} onChange={(event) => setQ(event.target.value)} placeholder="Search name, role, doctor, or organization" className="max-w-md" data-testid="relationship-contact-search" />
          <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={!active} onChange={(event) => setActive(!event.target.checked)} /> Show inactive</label>
        </div>
        {filtered.length === 0 ? (
          <div className="border rounded-lg bg-white p-10 text-center text-sm text-slate-500">
            <ContactRound className="w-8 h-8 mx-auto mb-2 text-slate-300" /> No relationship contacts found.
          </div>
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-x-auto bg-white">
            <table className="w-full text-sm" data-testid="relationship-contacts-table">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider"><tr>
                <th className="text-left px-4 py-3">Name / Role</th><th className="text-left px-4 py-3">Contact</th>
                <th className="text-left px-4 py-3">Doctors</th><th className="text-left px-4 py-3">Organizations</th>
                <th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3">Actions</th>
              </tr></thead>
              <tbody>{filtered.map((row) => <tr key={row.id} className="border-t align-top">
                <td className="px-4 py-3"><div className="font-medium">{row.full_name}{row.is_primary && <span className="ml-2 text-xs text-emerald-700">Primary</span>}</div><div className="text-slate-500">{row.role_type}</div></td>
                <td className="px-4 py-3 text-slate-600"><div>{row.email || "—"}</div><div>{row.phone || "—"}</div></td>
                <td className="px-4 py-3 text-slate-600">{row.linked_contacts.map((item) => item.name).join(", ") || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{row.linked_organizations.map((item) => item.name).join(", ") || "—"}</td>
                <td className="px-4 py-3"><span className={row.active ? "text-emerald-700" : "text-slate-500"}>{row.active ? "Active" : "Inactive"}</span>{row.end_date && <div className="text-xs text-slate-400">Ended {row.end_date}</div>}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {canEdit && row.active && <button onClick={() => startEdit(row)} className="text-xs text-sky-700 mr-3"><Pencil className="w-3.5 h-3.5 inline mr-1" />Edit</button>}
                  {((row.active && canDeactivate) || (!row.active && canRestore)) && <button onClick={() => changeLifecycle(row)} className="text-xs text-slate-600">{row.active ? <UserMinus className="w-3.5 h-3.5 inline mr-1" /> : <RotateCcw className="w-3.5 h-3.5 inline mr-1" />}{row.active ? "Deactivate" : "Reactivate"}</button>}
                </td>
              </tr>)}</tbody>
            </table>
          </div>
        )}
      </main>
      <Dialog open={open} onOpenChange={setOpen}><DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{editing ? "Edit relationship contact" : "Add relationship contact"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Full name</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
          <div><Label>Role / type</Label><input list="relationship-roles" value={form.role_type} onChange={(e) => setForm({ ...form, role_type: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" /><datalist id="relationship-roles">{roles.map((role) => <option key={role} value={role} />)}</datalist></div>
          <div className="flex items-end pb-2"><label className="text-sm flex gap-2"><input type="checkbox" checked={form.is_primary} onChange={(e) => setForm({ ...form, is_primary: e.target.checked })} /> Primary contact person</label></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <div><Label>Start date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
          <div><Label>End date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
          <div><Label>Doctors / leads</Label><select multiple value={form.contact_ids} onChange={(e) => setForm({ ...form, contact_ids: toggleValues(e) })} className="w-full min-h-28 border rounded-md p-2 text-sm">{contacts.map((item) => <option key={item.id} value={item.id}>{`${item.first_name || ""} ${item.last_name || ""}`.trim()}</option>)}</select><p className="text-xs text-slate-400 mt-1">Ctrl/Cmd-click to select several.</p></div>
          <div><Label>Organizations / clinics</Label><select multiple value={form.organization_ids} onChange={(e) => setForm({ ...form, organization_ids: toggleValues(e) })} className="w-full min-h-28 border rounded-md p-2 text-sm">{organizations.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select><p className="text-xs text-slate-400 mt-1">Ctrl/Cmd-click to select several.</p></div>
          <div className="col-span-2"><Label>Notes</Label><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full min-h-24 border rounded-md p-2 text-sm" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} disabled={saving || !form.full_name.trim() || (!form.contact_ids.length && !form.organization_ids.length)}>{saving ? "Saving…" : "Save"}</Button></DialogFooter>
      </DialogContent></Dialog>
    </>
  );
}
